/**
 * `useScheduleJobs` — the Agendamentos pane's read side (agendamentos plan P1;
 * R2 handoff §2). One place to `schedule.list` + `schedule.runs`, joined so
 * every job row carries its LAST RUN, with the R2 freshness discipline:
 *
 *  - subscribe `plugin.schedule.changed` (the `plugin.` prefix is load-bearing
 *    — `useLexEventStream` owns it) and refetch on any change;
 *  - keep a slow poll fallback — the WS broadcast-delivery finding says a
 *    broadcast may simply not arrive; broadcasts are an optimization, never
 *    the source of truth;
 *  - `SCHEDULE_DISABLED` is a STATE, not an error: the runtime's message is
 *    already user-facing PT-BR and the pane shows it verbatim (§2.3).
 *
 * Read-only; the mutations (create/update/pause/resume/delete) live with the
 * dialogs that own them (P2) and simply call `refresh()` after writing.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { useGatewayStore } from "@/stores/useGatewayStore";
import { useLexEventStream } from "@/hooks/useEventStream";

/** Handoff §2.1 — the shape every schedule.* method returns. */
export interface ScheduleJobView {
  id: string;
  title: string;
  kind: "reminder" | "task" | "watch" | "mission" | "system";
  enabled: boolean;
  locked: boolean;
  createdBy: "user" | "system";
  scheduleHuman: string;
  schedule:
    | { kind: "at"; at: string }
    | { kind: "every"; everyMs: number }
    | { kind: "cron"; expr: string; tz: string; skipWeekends?: boolean };
  costTier: "cheap" | "low" | "med";
  delivery: { mode: "none" | "owner" };
  caseId?: string;
  spec:
    | { kind: "reminder"; text: string }
    | { kind: "task"; instruction: string }
    | { kind: "watch"; source: string; instruction?: string }
    | { kind: "mission"; brief: string }
    | { kind: "system"; tools: string[]; contract: string; preconditions?: string[] };
  createdAt: string;
  updatedAt: string;
}

/** Handoff §2.5. */
export interface ScheduleRunRow {
  ts: string;
  jobId: string;
  kind: string;
  title: string;
  status: "ok" | "error" | "skipped";
  summary?: string;
  durationMs?: number;
  delivered?: boolean;
  deliveryStatus?: string;
  error?: string;
}

export type ScheduleGate =
  | { state: "loading" }
  | { state: "ready" }
  /** Org kill-switch / break-glass — `message` is the runtime's own PT-BR. */
  | { state: "disabled"; message: string }
  /** Older runtime without `schedule.*`, or a hard failure. */
  | { state: "unavailable"; message: string };

/** Poll floor beneath the broadcast — slow on purpose (fallback, not engine). */
const POLL_MS = 45_000;

function errorInfo(err: unknown): { code?: string; message: string } {
  const message = err instanceof Error ? err.message : String(err);
  // Gateway errors arrive as Error with the runtime's `message`; the code
  // rides the message text for our purposes (SCHEDULE_DISABLED is distinct
  // enough to match on).
  return { message };
}

export function useScheduleJobs(): {
  gate: ScheduleGate;
  jobs: ScheduleJobView[];
  /** jobId → its most recent run (newest-first source). */
  lastRun: Map<string, ScheduleRunRow>;
  refresh: () => void;
} {
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const connected = connectionState === "connected";

  const [gate, setGate] = useState<ScheduleGate>({ state: "loading" });
  const [jobs, setJobs] = useState<ScheduleJobView[]>([]);
  const [lastRun, setLastRun] = useState<Map<string, ScheduleRunRow>>(new Map());
  const inflight = useRef(false);

  const refresh = useCallback(() => {
    if (!rpc || !connected || inflight.current) return;
    inflight.current = true;
    void (async () => {
      try {
        const list = await rpc.call<{ jobs?: ScheduleJobView[] }>("schedule.list", {});
        const next = list?.jobs ?? [];
        setJobs(next);
        setGate({ state: "ready" });
        // Last results — one page of the newest runs covers the join for a
        // 20-job cap surface; a per-job drawer (P3) reads its own history.
        try {
          const runs = await rpc.call<{ runs?: ScheduleRunRow[] }>("schedule.runs", {
            limit: 100,
          });
          const byJob = new Map<string, ScheduleRunRow>();
          for (const r of runs?.runs ?? []) {
            if (!byJob.has(r.jobId)) byJob.set(r.jobId, r); // newest-first
          }
          setLastRun(byJob);
        } catch {
          // Run-log unavailable is not a pane failure — rows show no result.
        }
      } catch (err) {
        const { message } = errorInfo(err);
        if (/SCHEDULE_DISABLED/i.test(message)) {
          setGate({ state: "disabled", message });
        } else if (/method|unknown|not.?found|unsupported/i.test(message)) {
          setGate({
            state: "unavailable",
            message,
          });
        } else {
          setGate({ state: "unavailable", message });
        }
      } finally {
        inflight.current = false;
      }
    })();
  }, [rpc, connected]);

  // Initial read + re-read on (re)connect.
  useEffect(() => refresh(), [refresh]);

  // Broadcast-driven freshness (optimization)…
  useLexEventStream("schedule.changed", () => refresh());

  // …with the mandatory slow poll fallback (source of truth).
  useEffect(() => {
    if (!connected) return;
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [connected, refresh]);

  return { gate, jobs, lastRun, refresh };
}
